import type { FontSizeConfig, FontSizeValue } from './types';

export const desktopBreakpoint = 928;
export const tabletBreakpoint = 496;

// iphone X as default width (could be smaller or bigger, but these +- 20 till 40px won't make a difference)
export const maxWidth = 375;
export const maxWidthAnswer = 155.5;

// won't be exceeded in the build campaign
export const maxWidthTablet = 448;
export const maxWidthTabletAnswer = 216;
export const maxWidthDesktop = 608;
export const maxWidthDesktopAnswer = 216;
export const maxWidthUnframed = 960;
export const maxImageRatio = 200;

// Actions
export const LONGPRESS_TIMEOUT = 250;

// Images
export const sizeRatioDetails = {
    small: {
        width: 16,
        height: 9,
    },
    medium: {
        width: 4,
        height: 3,
    },
    large: {
        width: 6,
        height: 5,
    },
    xLarge: {
        width: 1,
        height: 1,
    },
    answer: {
        width: 4,
        height: 3,
    },
    listItem: {
        width: 1,
        height: 1,
    },
};

export const imgixAr = {
    // 16 / 9
    small: sizeRatioDetails.small.width / sizeRatioDetails.small.height,
    // 4 / 3
    medium: sizeRatioDetails.medium.width / sizeRatioDetails.medium.height,
    // 6 / 5,
    large: sizeRatioDetails.large.width / sizeRatioDetails.large.height,
    // 1 / 1
    xLarge: sizeRatioDetails.xLarge.width / sizeRatioDetails.xLarge.height,
    // 4 / 3
    answer: sizeRatioDetails.answer.width / sizeRatioDetails.answer.height,
    // 1 / 1
    listItem: sizeRatioDetails.listItem.width / sizeRatioDetails.listItem.height,
};

export const sizeRatioMap = {
    // 16 / 9
    small: (sizeRatioDetails.small.height * 100) / sizeRatioDetails.small.width,
    // 4 / 3
    medium: (sizeRatioDetails.medium.height * 100) / sizeRatioDetails.medium.width,
    // 6 / 5,
    large: (sizeRatioDetails.large.height * 100) / sizeRatioDetails.large.width,
    // 1 / 1
    xLarge: (sizeRatioDetails.xLarge.height * 100) / sizeRatioDetails.xLarge.width,
    // 4 / 3
    answer: (sizeRatioDetails.answer.height * 100) / sizeRatioDetails.answer.width,
    // 1 / 1
    listItem: (sizeRatioDetails.listItem.height * 100) / sizeRatioDetails.listItem.width,
};

// Calendly
export const CALENDLY_URL_REGEX = /(https?:\/\/calendly\.com)\/([a-z0-9-_@+]*)\/([a-z0-9-_@+]+)/;
export const CALENDLY_EVENT_SCHEDULED = 'calendly.event_scheduled';

// Cal.com
export const CAL_URL_REGEX = /^https:\/\/([a-z0-9-]+\.)?cal\.(com|eu)\/[^/]+\/.+$/;
export const CAL_EVENT_SCHEDULED = 'bookingSuccessful';
export const CAL_EVENT_SCHEDULED_V2 = 'bookingSuccessfulV2';

// GoHighLevel (GHL)
export const GHL_URL_REGEX = /^https:\/\/api\.leadconnectorhq\.com\/widget\/bookings?\/.+$/;
export const GHL_EVENT_SCHEDULED = 'msgsndr-booking-complete';

// HubSpot Meetings
export const HUBSPOT_URL_REGEX = /^https:\/\/meetings-[^.]+\.hubspot\.com\/.+$/;
export const HUBSPOT_EVENT_SCHEDULED = 'meetingBookSucceeded';

// iClosed
export const ICLOSED_URL_REGEX = /^https:\/\/app\.iclosed\.io\/e\/[^/]+\/[^/]+\/?$/;
export const ICLOSED_EVENT_SCHEDULED = 'iclosed.call_scheduled';

// Our internal field names that can be mapped to Calendar providers built-in fields
export const NAMED_ANSWERS = [
    'name',
    'email',
    'firstName',
    'lastName',
    'phone',
    'fname',
    'lname',
    'company',
];

// Font sizes

// This is the single source of truth
// regarding font sizes for the TipTap text displayment.
export const FONT_SIZES: FontSizeConfig = {
    checkbox: {
        internal: true,
        advanced: false,
        labelI18nKey: 'blocks:font-size-small',
        abbreviationI18nKey: 'blocks:font-size-abbreviation-small',
        base: {
            sizePx: 14,
            lineHeight: 1.5,
            letterSpacing: 'normal',
        },
        desktop: {
            sizePx: 14,
            lineHeight: 1.5,
            letterSpacing: 'normal',
        },
    },
    '14px': {
        base: {
            sizePx: 14,
            lineHeight: 1.5,
            letterSpacing: 'normal',
        },
        desktop: {
            sizePx: 14,
            lineHeight: 1.5,
            letterSpacing: 'normal',
        },
        advanced: true,
    },
    small: {
        advanced: false,
        labelI18nKey: 'blocks:font-size-small',
        abbreviationI18nKey: 'blocks:font-size-abbreviation-small',
        base: {
            sizePx: 16,
            lineHeight: 1.5,
            letterSpacing: 'normal',
        },
        desktop: {
            sizePx: 16,
            lineHeight: 1.5,
            letterSpacing: 'normal',
        },
    },
    '18px': {
        base: {
            sizePx: 18,
            lineHeight: 1.5,
            letterSpacing: 'normal',
        },
        desktop: {
            sizePx: 20,
            lineHeight: 1.25,
            letterSpacing: 'normal',
        },
        advanced: true,
    },
    medium: {
        advanced: false,
        labelI18nKey: 'blocks:font-size-medium',
        abbreviationI18nKey: 'blocks:font-size-abbreviation-medium',
        base: {
            sizePx: 20,
            lineHeight: 1.25,
            letterSpacing: 'normal',
        },
        desktop: {
            sizePx: 24,
            lineHeight: 1.25,
            letterSpacing: 'normal',
        },
    },
    large: {
        advanced: false,
        labelI18nKey: 'blocks:font-size-large',
        abbreviationI18nKey: 'blocks:font-size-abbreviation-large',
        base: {
            sizePx: 24,
            lineHeight: 1.25,
            letterSpacing: 'normal',
        },
        tablet: {
            sizePx: 30,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
        },
        desktop: {
            sizePx: 36,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
        },
    },
    xLarge: {
        advanced: false,
        labelI18nKey: 'blocks:font-size-xLarge',
        abbreviationI18nKey: 'blocks:font-size-abbreviation-xLarge',
        base: {
            sizePx: 30,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
        },
        tablet: {
            sizePx: 36,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
        },
        desktop: {
            sizePx: 48,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
        },
    },
    '40px': {
        base: {
            sizePx: 40,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
        },
        tablet: {
            sizePx: 46,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
        },
        desktop: {
            sizePx: 56,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
        },
        advanced: true,
    },
    '48px': {
        base: {
            sizePx: 48,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        desktop: {
            sizePx: 64,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        advanced: true,
    },
    '64px': {
        base: {
            sizePx: 64,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        desktop: {
            sizePx: 80,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        advanced: true,
    },
};

export const FontSizeValues = Object.keys(FONT_SIZES) as FontSizeValue[];

export const VALID_COUNTRY_CODES = [
    'AC',
    'AD',
    'AE',
    'AF',
    'AG',
    'AI',
    'AL',
    'AM',
    'AO',
    'AR',
    'AS',
    'AT',
    'AU',
    'AW',
    'AX',
    'AZ',
    'BA',
    'BB',
    'BD',
    'BE',
    'BF',
    'BG',
    'BH',
    'BI',
    'BJ',
    'BL',
    'BM',
    'BN',
    'BO',
    'BQ',
    'BR',
    'BS',
    'BT',
    'BW',
    'BY',
    'BZ',
    'CA',
    'CC',
    'CD',
    'CF',
    'CG',
    'CH',
    'CI',
    'CK',
    'CL',
    'CM',
    'CN',
    'CO',
    'CR',
    'CU',
    'CV',
    'CW',
    'CX',
    'CY',
    'CZ',
    'DE',
    'DJ',
    'DK',
    'DM',
    'DO',
    'DZ',
    'EC',
    'EE',
    'EG',
    'EH',
    'ER',
    'ES',
    'ET',
    'FI',
    'FJ',
    'FK',
    'FM',
    'FO',
    'FR',
    'GA',
    'GB',
    'GD',
    'GE',
    'GF',
    'GG',
    'GH',
    'GI',
    'GL',
    'GM',
    'GN',
    'GP',
    'GQ',
    'GR',
    'GT',
    'GU',
    'GW',
    'GY',
    'HK',
    'HN',
    'HR',
    'HT',
    'HU',
    'ID',
    'IE',
    'IL',
    'IM',
    'IN',
    'IO',
    'IQ',
    'IR',
    'IS',
    'IT',
    'JE',
    'JM',
    'JO',
    'JP',
    'KE',
    'KG',
    'KH',
    'KI',
    'KM',
    'KN',
    'KP',
    'KR',
    'KW',
    'KY',
    'KZ',
    'LA',
    'LB',
    'LC',
    'LI',
    'LK',
    'LR',
    'LS',
    'LT',
    'LU',
    'LV',
    'LY',
    'MA',
    'MC',
    'MD',
    'ME',
    'MF',
    'MG',
    'MH',
    'MK',
    'ML',
    'MM',
    'MN',
    'MO',
    'MP',
    'MQ',
    'MR',
    'MS',
    'MT',
    'MU',
    'MV',
    'MW',
    'MX',
    'MY',
    'MZ',
    'NA',
    'NC',
    'NE',
    'NF',
    'NG',
    'NI',
    'NL',
    'NO',
    'NP',
    'NR',
    'NU',
    'NZ',
    'OM',
    'PA',
    'PE',
    'PF',
    'PG',
    'PH',
    'PK',
    'PL',
    'PM',
    'PR',
    'PS',
    'PT',
    'PW',
    'PY',
    'QA',
    'RE',
    'RO',
    'RS',
    'RU',
    'RW',
    'SA',
    'SB',
    'SC',
    'SD',
    'SE',
    'SG',
    'SH',
    'SI',
    'SJ',
    'SK',
    'SL',
    'SM',
    'SN',
    'SO',
    'SR',
    'SS',
    'ST',
    'SV',
    'SX',
    'SY',
    'SZ',
    'TA',
    'TC',
    'TD',
    'TG',
    'TH',
    'TJ',
    'TK',
    'TL',
    'TM',
    'TN',
    'TO',
    'TR',
    'TT',
    'TV',
    'TW',
    'TZ',
    'UA',
    'UG',
    'US',
    'UY',
    'UZ',
    'VA',
    'VC',
    'VE',
    'VG',
    'VI',
    'VN',
    'VU',
    'WF',
    'WS',
    'XK',
    'YE',
    'YT',
    'ZA',
    'ZM',
    'ZW',
] as const;
